/*
 * Provides functionality for associating names with lists of functions
 * and functionality to execute that list of methods via notify. This
 * is not tied into the system events.
 */
// A handler returns true when it handled the event.
export type EventHandler<E = unknown> = (e: E) => unknown;

interface HandlerEntry<E> {
  func: EventHandler<E>;
  context: unknown;
}

export default class EventDispatcher<E = unknown> {
  declare _handlers: Record<string, HandlerEntry<E>[]>;

  constructor() {
    this._handlers = {};
  }

  on(name: string, func: EventHandler<E>, context?: unknown) {
    let array = this._handlers[name];
    if (array === undefined) {
      array = [];
      this._handlers[name] = array;
    }
    array.push({ func: func, context: context });
  }

  off(name: string, func: EventHandler<E>, context?: unknown) {
    const array = this._handlers[name];
    if (array) {
      const index = this._find(array, func, context);
      if (index > -1) {
        array.splice(index, 1);
      }
      if (array.length === 0) {
        delete this._handlers[name];
      }
    }
  }

  dispatch(name: string, e?: E, context?: unknown): boolean {
    const array = this._handlers[name];
    let handled = false;
    if (array) {
      for (let n = 0, max = array.length; n < max; n++) {
        const item = array[n];
        const ctx = context || item.context;
        if (ctx) {
          handled = (item.func.call(ctx, e as E) as boolean) || handled;
        } else {
          handled = (item.func(e as E) as boolean) || handled;
        }
      }
    }
    return handled;
  }

  _find(
    array: HandlerEntry<E>[] | undefined,
    func: EventHandler<E>,
    context?: unknown,
  ): number {
    if (array) {
      for (let n = 0, max = array.length; n < max; n++) {
        const item = array[n];
        if (
          item.func === func &&
          (context == null || item.context === context)
        ) {
          return n;
        }
      }
    }

    return -1;
  }
}
